import { useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import HelloprenupLogoWithBlueBackground from "../../icons/HelloprenupLogoWithBlueBackground";
import HelloprenupLogoWithText from "../../icons/HelloprenupLogoWithText";
import { PageLoading } from "../../Loading";
import ErrorMessage, { PageErrorContainer } from "../../Error";
import NavBar from "../utils/desktop/NavBar";
import NavItemsContainer from "../utils/desktop/NavItemsContainer";
import NavItem from "../utils/desktop/NavItem";
import { adminNavItems, bottomAdminNavItems } from "../utils/adminNav";
import { COLOR_APP_TEXT } from "../../theme";
import { ParagraphHeaderSmall } from "../../typography/Paragraph";
import Divider from "../../Divider";
import Signout from "../../icons/Signout";
import { SecondaryButtonSmall } from "../../buttons/SecondaryButton";
import WarningPopup from "../../popups/Warning";
import Footer from "../Footer";
import { User } from "../../../../../api/src/types";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import {
  StyledCircleIconButton,
  SignoutAndFooter,
  Spacer,
  NavLogo,
  IconContainer,
  HelloPrenupLogoWithTextContainer,
  FooterContainer,
  Link,
} from "../utils/desktop/components";
import { useLocation } from "react-router";

const AdminNav = ({
  isLoading = false,
  error,
}: {
  user?: User | undefined;
  isLoading?: boolean;
  error?: boolean | FetchBaseQueryError | SerializedError | undefined;
} = {}) => {
  const { signOut } = useClerk();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLogoutPopup, setShowLogoutPopup] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  if (isLoading) {
    return (
      <NavBar
        isExpanded={isExpanded}
        onMouseEnter={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <PageLoading data-testid="loading" />
      </NavBar>
    );
  }

  if (error) {
    return (
      <NavBar
        isExpanded={isExpanded}
        onMouseEnter={() => {
          setIsExpanded(true);
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <PageErrorContainer>
          <ErrorMessage>Error occurred</ErrorMessage>
        </PageErrorContainer>
      </NavBar>
    );
  }

  const handleLogout = () => {
    setIsExpanded(false);
    setShowLogoutPopup(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutPopup(false);
    dispatch({ type: "USER_LOGOUT" });
    await signOut({ redirectUrl: "/login" });
  };

  const handleCancelLogout = () => {
    setShowLogoutPopup(false);
  };

  return (
    <>
      {showLogoutPopup && (
        <WarningPopup
          title="Are you sure you want to log out?"
          buttons={{
            confirmText: "Log out",
            cancelText: "Cancel",
            onConfirm: handleConfirmLogout,
            onCancel: handleCancelLogout,
          }}
        />
      )}
      <NavBar
        role="navigation"
        isExpanded={isExpanded}
        onMouseOver={() => {
          if (!isExpanded) {
            setIsExpanded(true);
          }
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
        }}
      >
        <NavLogo isExpanded={isExpanded}>
          {isExpanded ? (
            <HelloPrenupLogoWithTextContainer data-testid="helloprenup-logo-with-text">
              <HelloprenupLogoWithText />
            </HelloPrenupLogoWithTextContainer>
          ) : (
            <div data-testid="helloprenup-logo-with-background">
              <HelloprenupLogoWithBlueBackground />
            </div>
          )}
        </NavLogo>
        <NavItemsContainer>
          {adminNavItems.map((item) => (
            <Link to={item.path} key={item.label}>
              <NavItem
                key={item.label}
                isActive={location.pathname === item.path}
                isExpanded={isExpanded}
              >
                <IconContainer>{item.icon}</IconContainer>
                {isExpanded && (
                  <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                    {item.label}
                  </ParagraphHeaderSmall>
                )}
              </NavItem>
            </Link>
          ))}
          <Spacer />
        </NavItemsContainer>
        <SignoutAndFooter>
          <Divider />
          {bottomAdminNavItems.map((item) => (
            <Link to={item.path} key={item.label}>
              <NavItem
                key={item.label}
                isActive={location.pathname === item.path}
                isExpanded={isExpanded}
              >
                <IconContainer>{item.icon}</IconContainer>
                {isExpanded && (
                  <ParagraphHeaderSmall color={COLOR_APP_TEXT}>
                    {item.label}
                  </ParagraphHeaderSmall>
                )}
              </NavItem>
            </Link>
          ))}
          {isExpanded ? (
            <SecondaryButtonSmall onClick={handleLogout}>
              Sign&nbsp;out
            </SecondaryButtonSmall>
          ) : (
            <StyledCircleIconButton onClick={handleLogout}>
              <Signout />
            </StyledCircleIconButton>
          )}
          {isExpanded && (
            <FooterContainer>
              <Footer />
            </FooterContainer>
          )}
        </SignoutAndFooter>
      </NavBar>
    </>
  );
};

export default AdminNav;
